import InAppMessage from '../../components/InAppMessage';
import React, { useState, useEffect } from 'react';
import SampleConceptViewer from '../../components/SampleConceptViewer';
import SampleFeasibilityViewer from '../../components/SampleFeasibilityViewer';
import { useAppContext } from '../../context/AppContext';
import { db } from '../../config/firebase';
import { doc, setDoc } from 'firebase/firestore';
import PricingTableModal from '../../components/PricingTableModal';
import { getPaymentLink, getStage1Price } from '../../config/paymentLinks';

export default function Dashboard12() {
  const {
    currentUser,
    projects,
    setProjects,
    typeNames,
    calculateStage2PaymentStructure,
    setShowProjectModal,
    setShowPaymentModal,
    setPaymentProject,
    setShowRevisionRequestModal,
    setRevisionRequestProject,
    setRevisionData,
    logout,
    approveProject,
    showNotification,
    isAdminUser,
    inAppMessages,
    dismissMessage,
    showSampleViewer,
    setShowSampleViewer,
    showFeasibilityViewer, 
    setShowFeasibilityViewer 
  } = useAppContext();

  const [showRelatedProjects, setShowRelatedProjects] = useState({});
  const [replyMessages, setReplyMessages] = useState({});
  const [showMessaging, setShowMessaging] = useState({});
  const [showDeliverables, setShowDeliverables] = useState({});
  const [showPricingTable, setShowPricingTable] = useState(false);

  // Filter only Stage 1 & 2 projects - FIX: Add unique key check
  const userProjects = React.useMemo(() => {
    const seen = new Set();
    return projects.filter(p => {
      // Check if we've seen this project ID already
      if (seen.has(p.id)) return false;
      
      // Check if it's a Stage 1 or 2 project for this user
      const isUserProject = p.stage <= 2 && (
        p.email === currentUser.email || 
        p.client === currentUser.name || 
        p.client === currentUser.email
      );
      
      if (isUserProject) {
        seen.add(p.id);
        return true;
      }
      return false;
    });
  }, [projects, currentUser]);

  const findRelatedProjects = (project) => {
    if (!project.address) return [];
    
    const normalizedAddress = project.address.toLowerCase().trim();
    const addressParts = normalizedAddress.substring(0, 30);
    
    return userProjects.filter(p => {
      if (p.id === project.id) return false;
      
      const pAddress = p.address?.toLowerCase().trim() || '';
      if (!pAddress) return false;
      
      return pAddress.includes(addressParts) || normalizedAddress.includes(pAddress.substring(0, 30));
    }).sort((a, b) => a.stage - b.stage);
  };

  const hasCompletedStage1ForAddress = (project) => {
    if (project.stage !== 2) return null;
    
    const relatedProjects = findRelatedProjects(project);
    const stage1Projects = relatedProjects.filter(p => p.stage === 1);
    
    if (stage1Projects.length === 0) return false;
    
    return stage1Projects.some(p => 
      p.status === 'stage-1-approved' || 
      p.status === 'stage-1-completed' || 
      p.status === 'paid' ||
      p.paymentStatus === 'paid'
    );
  };

  const toggleMessaging = (projectId) => {
    setShowMessaging(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const toggleRelatedProjects = (projectId) => {
    setShowRelatedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const toggleDeliverables = (projectId) => {
    setShowDeliverables(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const isProjectCompleted = (project) => {
    return project.status === 'completed' ||
      project.status === 'stage-1-completed' ||
      project.status === 'stage-2-completed';
  };

  const getNextPaymentAmount = (project) => {
    if (project.stage !== 2 || !project.paymentStructure) return null;

    const { totalPayments, paymentAmounts } = project.paymentStructure;
    const paymentsMade = project.paymentsMade || 0;

    if (paymentsMade < totalPayments) {
      return paymentAmounts[paymentsMade];
    }
    return null;
  };

  // ============================================
  // UPDATED: getUserStatusMessage for new Stage 1 flow
  // ============================================
  const getUserStatusMessage = (project) => {
    // Stage 2 quote statuses
    if (project.stage === 2 && project.status === 'quote-in-progress') {
      return '📋 Site complexity assessment in progress - quote will be provided within 2-3 business days';
    }
    
    if (project.stage === 2 && project.status === 'quote-ready') {
      return '📋 Quote ready for review - additional fees may apply for site complexity';
    }
    
    if (project.stage === 2 && project.status === 'quote-approved') {
      return '✅ Quote approved - awaiting initial payment to begin work';
    }

    // ============================================
    // STAGE 1 - NEW PAYMENT MODEL STATUSES
    // ============================================
    if (project.stage === 1) {
      // Card capture needed
      if (project.status === 'awaiting-card-capture' || 
          (project.paymentStatus === 'pending' && !project.paymentMethodId)) {
        const price = project.paymentStructure?.paymentAmounts?.[0] || 199;
        return `💳 Card Details Required: Provide card details to begin work ($${price} hold placed on your card)`;
      }
      
      // Card captured / hold placed, work in progress
      if ((project.paymentStatus === 'card-captured' || project.paymentStatus === 'hold-placed') && project.status === 'in-progress') {
        return '⏳ Work in Progress: Your feasibility report is being prepared (3-5 business days)';
      }
      
      if (project.paymentStatus === 'card-captured' || project.paymentStatus === 'hold-placed') {
        return '✅ Card verified - Work will begin shortly';
      }
      
      // Draft delivered, invoice sent
      if (project.status === 'invoice-sent' || project.paymentStatus === 'invoice-sent') {
        const invoiceDate = project.invoiceSentAt ? new Date(project.invoiceSentAt) : null;
        let daysMessage = '';
        if (invoiceDate) {
          const daysRemaining = Math.max(0, 5 - Math.floor((Date.now() - invoiceDate.getTime()) / (1000 * 60 * 60 * 24)));
          daysMessage = ` (${daysRemaining} days remaining)`;
        }
        return `📧 Invoice Sent: Pay to receive final unwatermarked report${daysMessage}`;
      }
      
      // Payment completed
      if (project.status === 'paid' || project.paymentStatus === 'paid') {
        return '✅ Payment Complete: Final report available for download';
      }
      
      // Auto-charge failed
      if (project.paymentStatus === 'auto-charge-failed') {
        return '⚠️ Auto-charge Failed: Please update payment method or pay manually';
      }
      
      // Stage 1 completed/approved
      if (project.status === 'stage-1-completed' || project.status === 'stage-1-approved') {
        return '✅ Stage 1 Complete - Ready to proceed to Stage 2';
      }

      // Fallback for in-progress without card-captured status
      if (project.status === 'in-progress') {
        return '⏳ Work in Progress: Your feasibility report is being prepared';
      }
    }

    // Stage 2 payment statuses
    if (project.stage === 2) {
      if (project.status.includes('revision') && project.status.includes('complete')) {
        const revisionNumber = project.status.match(/revision-(\d+)-complete/)?.[1];
        return `✅ Revision ${revisionNumber || ''} completed - ready for review`;
      }
      
      if (project.paymentStructure) {
        const nextPaymentAmount = getNextPaymentAmount(project);
        if (nextPaymentAmount && project.status !== 'paid') {
          const paymentNum = (project.paymentsMade || 0) + 1;
          const totalPayments = project.paymentStructure.totalPayments;
          return `💳 Payment ${paymentNum}/${totalPayments} Required: $${nextPaymentAmount}`;
        }
        if (project.status === 'paid') {
          return '⏳ All payments received - Your project is being prepared by experienced design professionals';
        }
      }
      
      if (project.status === 'in-progress') {
        return '🔄 Work in progress - Your project is being prepared by our professional design team';
      }
      
      if (project.status === 'initial-concept-ready') {
        return '📥 Initial concept design ready for review';
      }
    }

    if (project.status.includes("revision-") && project.status.includes("-requested")) {
      const revisionNumber = project.status.match(/revision-(\d+)-requested/)[1];
      return `🔄 Revision ${revisionNumber} requested - being processed`;
    }

    return null;
  };

  const canApproveProject = (project) => {
    if (project.stage === 1) {
      return false;
    }

    if (project.stage === 2 && project.status === 'quote-ready') {
      return true;
    }

    if (!project.deliverables || project.deliverables.length === 0) return false;
    if (isProjectCompleted(project)) return false;

    if (project.stage === 2 && project.paymentStructure) {
      const hasPayments = project.paymentsMade >= 1;
      if (!hasPayments) return false;
    }

    const isReadyForApproval = project.status === "initial-concept-ready" ||
      project.status === "documentation-ready" ||
      project.status === "paid" || 
      (project.status.includes('revision-') && project.status.includes('-complete')) ||
      (project.documentsReady && project.stage <= 2);

    return isReadyForApproval;
  };

  const canRejectQuote = (project) => {
    return project.stage === 2 && project.status === "quote-ready";
  };

  const canRequestRevision = (project) => {
    if (project.stage === 1) {
      return false;
    }

    if (isProjectCompleted(project)) return false;

    if (project.stage === 2) {
      if (!project.revisions) return false;

      const hasDesignDelivered = project.deliverables && project.deliverables.length > 0;
      if (!hasDesignDelivered) return false;

      const hasRevisionComplete = 
        project.status?.includes('revision-') ||
        project.status?.includes('-complete') ||
        project.deliverables?.some(d => d.isRevision === true) ||
        project.communications?.some(c => 
          c.message?.includes('revised concept design is ready') ||
          c.message?.includes('Revision complete')
        );
      
      if (hasRevisionComplete) {
        return true;
      }

      if (project.paymentStructure) {
        const { totalPayments } = project.paymentStructure;
        const paymentsMade = project.paymentsMade || 0;
        
        if (totalPayments === 2) {
          if (paymentsMade < 1) return false;
        } else if (totalPayments === 3) {
          if (paymentsMade < 2) return false;
        }
      } else {
        const hasPayments = project.paymentsMade >= 1;
        if (!hasPayments) return false;
      }

      const isReviewable = 
        project.status === "initial-concept-ready" ||
        project.status === "documentation-ready" ||
        project.status === "paid" ||
        project.status === "in-progress" ||
        project.status === "awaiting-final-payment" ||
        project.status?.includes('revision') ||
        (project.deliverables && project.deliverables.length > 0) ||
        (project.communications && project.communications.some(c => 
          c.message?.includes('ready for your review') || 
          c.message?.includes('Initial Concept is ready') ||
          c.message?.includes('revised concept design is ready') ||
          c.message?.includes('concept design is ready')
        ));

      return isReviewable;
    }

    return false;
  };

  const canRequestComplexityQuote = (project) => {
    return isAdminUser() &&
           project.stage === 2 && 
           project.canRequestComplexityQuote && 
           project.status !== 'quote-in-progress' && 
           project.status !== 'quote-ready' &&
           project.status !== 'quote-approved' &&
           project.status !== 'quote-rejected';
  };

  const handleRequestComplexityQuote = async (project) => {
    try {
      const projectRef = doc(db, 'projects', project.id);
      
      await setDoc(projectRef, {
        status: 'quote-in-progress',
        requiresComplexityAssessment: true,
        quoteRequestedDate: new Date().toISOString(),
        communications: [
          ...(project.communications || []),
          {
            type: 'client',
            sender: project.client,
            message: 'I would like a custom quote for complex site/design requirements.',
            timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
          },
          {
            type: 'architect',
            sender: 'planAIdesign',
            message: 'We have received your request for a custom quote. Our team will assess the site complexity and provide a tailored quote within 2-3 business days.',
            timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
          }
        ]
      }, { merge: true });

      setProjects(prev => prev.map(p => {
        if (p.id === project.id) {
          return {
            ...p,
            status: 'quote-in-progress',
            requiresComplexityAssessment: true,
            quoteRequestedDate: new Date().toISOString(),
            communications: [
              ...(p.communications || []),
              {
                type: 'client',
                sender: project.client,
                message: 'I would like a custom quote for complex site/design requirements.',
                timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
              },
              {
                type: 'architect',
                sender: 'planAIdesign',
                message: 'We have received your request for a custom quote. Our team will assess the site complexity and provide a tailored quote within 2-3 business days.',
                timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
              }
            ]
          };
        }
        return p;
      }));

      showNotification(
        'Quote Requested',
        'Your request for a custom complexity quote has been submitted.',
        'success'
      );
    } catch (error) {
      console.error('Error requesting complexity quote:', error);
      showNotification('Error', 'Failed to request quote. Please try again.', 'error');
    }
  };

  const getRevisionButtonText = (project) => {
    if (project.stage === 1) {
      return "";
    }

    if (project.stage === 2) {
      if (!project.revisions) return "🔄 Request Revision";

      const currentRound = project.revisions.currentRound || 0;
      const maxIncluded = project.revisions.maxIncludedRevisions || 1;
      const totalRevisionsUsed = project.revisions.totalRevisionsUsed || 0;
      
      const hasRevisionPayment = project.revision1PaymentStatus === 'paid' ||
                                project.revision2PaymentStatus === 'paid' ||
                                project.revision3PaymentStatus === 'paid';
      
      const hasRevisionRequested = project.communications?.some(c => 
        c.type === 'client' && c.message?.includes('Revision') && c.message?.includes('requested')
      );
      
      const hasRevisionStatus = project.status?.includes('revision-') || 
                               project.status === 'revision-complete' ||
                               project.status?.includes('-complete');
      
      const hasRevisionTracking = currentRound > 0 || totalRevisionsUsed > 0;
      
      const hasRevisionDeliverable = project.deliverables?.some(d => 
        d.isRevision === true || 
        d.title?.toLowerCase().includes('revision') ||
        d.title?.toLowerCase().includes('revised')
      );
      
      const hasRevisionComplete = project.communications?.some(c => 
        c.message?.includes('revised concept design is ready') ||
        c.message?.includes('revision is ready') ||
        c.message?.includes('Revision complete')
      );
      
      const hasUsedIncludedRevision = hasRevisionPayment ||
                                     hasRevisionRequested || 
                                     hasRevisionStatus || 
                                     hasRevisionTracking ||
                                     hasRevisionDeliverable ||
                                     hasRevisionComplete ||
                                     currentRound >= maxIncluded ||
                                     totalRevisionsUsed >= maxIncluded;
      
      if (hasUsedIncludedRevision) {
        let revisionFee = project.revisions.additionalRevisionFee || 150;
        
        if (project.type === 'single' || project.type === 'duplex') {
          revisionFee = project.storeys === 1 ? 99 : 150;
        } else if (project.type === 'townhouse') {
          revisionFee = 150;
        } else if (project.type === 'secondary' || project.type === 'minor-renovation' || project.type === 'major-renovation') {
          revisionFee = 99;
        }

        return `🔄 Request Revision ($${revisionFee})`;
      } else {
        return "🔄 Request Revision (Included)";
      }
    }

    return "🔄 Request Revision";
  };

  // ============================================
  // UPDATED: shouldShowPaymentButton for new Stage 1 flow
  // ============================================
  const shouldShowPaymentButton = (project) => {
    if (project.stage === 1) {
      // Show button for card capture
      if (project.status === 'awaiting-card-capture' || 
          (project.paymentStatus === 'pending' && !project.paymentMethodId)) {
        return true;
      }
      
      // Show button for invoice payment
      if (project.status === 'invoice-sent' || project.paymentStatus === 'invoice-sent') {
        return true;
      }
      
      // Show button if auto-charge failed
      if (project.paymentStatus === 'auto-charge-failed') {
        return true;
      }
      
      // Already paid or in progress - don't show
      return false;
    }
    
    if (project.stage === 2) {
      return project.paymentStatus !== 'paid' && project.status !== 'paid';
    }
    return false;
  };

  // ============================================
  // UPDATED: getPaymentButtonText for new Stage 1 flow
  // ============================================
  const getPaymentButtonText = (project) => {
    if (project.stage === 1) {
      // Card capture needed
      if (project.status === 'awaiting-card-capture' || 
          (project.paymentStatus === 'pending' && !project.paymentMethodId)) {
        return '💳 Provide Card Details to Start';
      }
      
      // Payment due - invoice/payment request sent
      if (project.status === 'invoice-sent' || project.paymentStatus === 'invoice-sent' ||
          project.status === 'payment-due' || project.paymentStatus === 'payment-due') {
        const price = getStage1Price(project.type, project.townhouseUnits) || 
                      project.paymentStructure?.paymentAmounts?.[0] || 199;
        return `💰 Pay $${price} Now`;
      }
      
      // Auto-charge failed
      if (project.paymentStatus === 'auto-charge-failed' || project.paymentStatus === 'charge-failed') {
        const price = getStage1Price(project.type, project.townhouseUnits) || 
                      project.paymentStructure?.paymentAmounts?.[0] || 199;
        return `⚠️ Retry Payment ($${price})`;
      }
    }
    
    if (project.stage === 2) {
      const paymentsMade = project.paymentsMade || 0;
      const totalPayments = project.paymentStructure?.totalPayments || 2;
      const amount = project.paymentStructure?.paymentAmounts?.[paymentsMade] || 150;
      
      if (paymentsMade === 0) return `💳 Pay Initial $${amount}`;
      if (paymentsMade === totalPayments - 1) return `💳 Pay Final $${amount}`;
      return `💳 Pay $${amount}`;
    }
    
    return '💳 Make Payment';
  };

  const handlePaymentClick = (project) => {
    // For Stage 1 payment-due/invoice-sent, open payment link directly
    if (project.stage === 1 && 
        (project.status === 'invoice-sent' || project.paymentStatus === 'invoice-sent' ||
         project.status === 'payment-due' || project.paymentStatus === 'payment-due')) {
      
      // Use existing payment link if available, otherwise generate one
      const paymentLink = project.paymentLink || getPaymentLink(1, project.type, null, { 
        projectId: project.id,
        townhouseUnits: project.townhouseUnits
      });
      
      if (paymentLink) {
        window.open(paymentLink, '_blank');
        return;
      }
    }
    
    // For other cases, show payment modal
    setPaymentProject(project);
    setShowPaymentModal(true);
  };

  const handleRevisionRequest = (project) => {
    const currentRound = project.revisions?.currentRound || 0;
    const maxIncluded = project.revisions?.maxIncludedRevisions || 1;
    const nextRevisionNumber = currentRound + 1;
    const isAdditionalRevision = nextRevisionNumber > maxIncluded;
    
    let revisionFee = 0;
    if (isAdditionalRevision) {
      if (project.type === 'single' || project.type === 'duplex') {
        revisionFee = project.storeys === 1 ? 99 : 150;
      } else if (project.type === 'townhouse') {
        revisionFee = 150;
      } else if (project.type === 'secondary' || project.type === 'minor-renovation' || project.type === 'major-renovation') {
        revisionFee = 99;
      } else {
        revisionFee = project.revisions?.additionalRevisionFee || 150;
      }
    }
    
    setRevisionData({
      revisionNumber: nextRevisionNumber,
      isAdditional: isAdditionalRevision,
      fee: revisionFee
    });
    
    setRevisionRequestProject(project);
    setShowRevisionRequestModal(true);
  };

  const handleRejectQuote = (project) => {
    setProjects(prev => prev.map(p => {
      if (p.id === project.id) {
        const message = 'Thank you for your feedback. It is unfortunate we cannot assist you in this instance.';
        
        return {
          ...p,
          status: 'quote-rejected',
          rejectedDate: new Date().toISOString(),
          communications: [
            ...p.communications,
            {
              type: 'client',
              sender: project.client,
              message: 'I have decided not to proceed with the complexity quote.',
              timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
            },
            {
              type: 'architect',
              sender: 'planAIdesign',
              message: message,
              timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
            }
          ]
        };
      }
      return p;
    }));
    showNotification(
      'Quote Rejected',
      'The complexity quote has been declined.',
      'info'
    );
  };

  const sendMessage = (projectId, message) => {
    if (!message.trim()) {
      showNotification("Empty Message", "Please enter a message before sending", "warning");
      return;
    }
    showNotification("Message Sent", "Your message has been sent to planAIdesign", "success");
    setReplyMessages(prev => ({ ...prev, [projectId]: '' }));
  };

  const downloadFile = (projectName, fileName, fileUrl = null) => {
    if (fileUrl && fileUrl !== '#') {
      window.open(fileUrl, '_blank');
      showNotification("Download Started", `Downloading ${fileName}`, "success");
      return;
    }
    
    const content = `This is a placeholder for ${fileName} from project ${projectName}.\n\nIn a real application, this would contain the actual document content.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    showNotification("Download Started", `Downloading ${fileName}`, "success");
  };

  const downloadAllFiles = async (project, deliverable) => {
    // Download files sequentially to avoid popup blockers
    for (let i = 0; i < deliverable.files.length; i++) {
      const file = deliverable.files[i];
      
      await new Promise(resolve => setTimeout(resolve, i * 800)); // Stagger downloads
      
      if (typeof file === 'object' && file.url) {
        // For files with URLs, create an anchor and trigger download
        const a = document.createElement('a');
        a.href = file.url;
        a.download = file.name || `file-${i + 1}`;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        downloadFile(project.name, file);
      }
    }
    
    showNotification("Downloads Started", `Downloading ${deliverable.files.length} files`, "success");
  };

  // ============================================
  // Helper: Get status badge color
  // ============================================
  const getStatusBadgeColor = (project) => {
    const { status, paymentStatus } = project;
    
    // Stage 1 specific
    if (status === 'awaiting-card-capture') return 'bg-yellow-100 text-yellow-800';
    if (paymentStatus === 'card-captured' || paymentStatus === 'hold-placed') return 'bg-blue-100 text-blue-800';
    if (status === 'invoice-sent' || paymentStatus === 'invoice-sent') return 'bg-orange-100 text-orange-800';
    if (paymentStatus === 'auto-charge-failed') return 'bg-red-100 text-red-800';
    
    // General statuses
    if (status === 'paid' || paymentStatus === 'paid') return 'bg-green-100 text-green-800';
    if (status === 'in-progress') return 'bg-blue-100 text-blue-800';
    if (status === 'awaiting-payment') return 'bg-yellow-100 text-yellow-800';
    if (status.includes('complete')) return 'bg-green-100 text-green-800';
    if (status.includes('revision')) return 'bg-purple-100 text-purple-800';
    
    return 'bg-gray-100 text-gray-800';
  };

  // ============================================
  // Helper: Format status for display
  // ============================================
  const formatStatusDisplay = (project) => {
    const { status, paymentStatus } = project;
    
    // Stage 1 specific
    if (status === 'awaiting-card-capture') return 'Card Details Required';
    if ((paymentStatus === 'card-captured' || paymentStatus === 'hold-placed') && status === 'in-progress') return 'In Progress';
    if (paymentStatus === 'card-captured' || paymentStatus === 'hold-placed') return 'Card Verified';
    if (status === 'invoice-sent' || paymentStatus === 'invoice-sent' ||
        status === 'payment-due' || paymentStatus === 'payment-due') return 'Payment Due';
    if (paymentStatus === 'auto-charge-failed') return 'Payment Failed';
    
    // General mappings
    const statusMap = {
      'awaiting-payment': 'Awaiting Payment',
      'in-progress': 'In Progress',
      'paid': 'Paid',
      'stage-1-completed': 'Stage 1 Complete',
      'stage-1-approved': 'Stage 1 Approved',
      'initial-concept-ready': 'Concept Ready',
      'quote-in-progress': 'Quote In Progress',
      'quote-ready': 'Quote Ready',
      'quote-approved': 'Quote Approved'
    };
    
    return statusMap[status] || status.split('-').map(w => 
      w.charAt(0).toUpperCase() + w.slice(1)
    ).join(' ');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Updated Header with Professional Design Services (Compliant with Architect's Act NSW) */}
      <header className="bg-gradient-to-r from-slate-900 to-blue-700 text-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold">
                <span className="text-lg">plan</span>
                <span className="text-yellow-400 text-2xl">AI</span>
                <span className="text-lg">design</span>
                <span className="ml-2 text-sm">- Stage 1 & 2</span>
              </h1>
              <p className="text-xs text-gray-300 mt-1">
                Professional Design Services | NSW Compliant Documentation
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-100">
                Welcome, {currentUser.name}
                {isAdminUser() && (
                  <span className="ml-2 bg-purple-600 px-2 py-1 rounded text-xs">Admin</span>
                )}
              </span>
              <button
                onClick={logout}
                className="text-sm text-gray-200 hover:text-white"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* In-App Messages - Removed to reduce duplicate create buttons */}

      {/* Welcome Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <div className="bg-green-50 border-l-4 border-green-500 p-3 mb-4 rounded-lg">
          <p className="text-sm font-semibold mb-1">
            Welcome to your online design dashboard
          </p>
          <p className="text-xs text-gray-700">
            All communication, updates, and deliverables are managed right here. 
            Track your project progress, download documents, and request revisions - all in one place.
          </p>
        </div>
      </div>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
            <div className="text-3xl font-bold text-gray-900">{userProjects.length}</div>
            <div className="text-sm text-gray-600">Total Projects (Stage 1 & 2)</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
            <div className="text-3xl font-bold text-blue-600">
              {userProjects.filter(p => !isProjectCompleted(p)).length}
            </div>
            <div className="text-sm text-gray-600">Active Projects</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
            <div className="text-3xl font-bold text-green-600">
              {userProjects.filter(p => isProjectCompleted(p)).length}
            </div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
            <h2 className="text-xl font-semibold">Your Stage 1 & 2 Projects</h2>
            <button
              onClick={() => setShowProjectModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-md"
            >
              + New Project
            </button>
          </div>

          <div className="p-6">
            {userProjects.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 text-5xl mb-4">📋</div>
                <p className="text-gray-600 mb-4">No Stage 1 or 2 projects yet</p>
                <p className="text-gray-500 text-sm">Click "+ New Project" above to get started</p>
              </div>
            ) : (
              <div className="space-y-4">
                {userProjects.map(project => (
                  <div key={`project-${project.id}`} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-semibold">{project.name}</h3>
                        <p className="text-sm text-gray-600">
                          {typeNames[project.type]}{project.type === 'townhouse' && project.townhouseUnits ? ` (${project.townhouseUnits} units)` : ''} | Stage {project.stage}
                        </p>
                        {/* Status Badge */}
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeColor(project)}`}>
                          {formatStatusDisplay(project)}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm text-gray-500">
                          Created: {project.createdDate}
                        </span>
                      </div>
                    </div>

                    {/* Status Message */}
                    {getUserStatusMessage(project) && (
                      <div className="bg-yellow-50 p-2 rounded mb-3">
                        <p className="text-sm">{getUserStatusMessage(project)}</p>
                      </div>
                    )}

                    {/* Payment Due Alert for Stage 1 */}
                    {project.stage === 1 && (project.status === 'invoice-sent' || project.paymentStatus === 'invoice-sent' ||
                                              project.status === 'payment-due' || project.paymentStatus === 'payment-due') && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div>
                            <p className="text-sm font-medium text-yellow-800">📄 Report Ready - Payment Due</p>
                            <p className="text-xs text-yellow-700 mt-1">
                              Pay within 5 business days or card will be auto-charged
                            </p>
                          </div>
                          <button
                            onClick={() => handlePaymentClick(project)}
                            className="bg-green-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-green-700"
                          >
                            💳 Pay ${getStage1Price(project.type, project.townhouseUnits) || 199} Now
                          </button>
                        </div>
                        {/* Invoice Download */}
                        {project.invoice && project.invoice.url && (
                          <div className="mt-3 pt-3 border-t border-yellow-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-yellow-800">📋 Tax Invoice</span>
                                <span className="text-xs text-yellow-600">{project.invoice.name}</span>
                              </div>
                              <a
                                href={project.invoice.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download={project.invoice.name}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                Download
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Receipt Download for Stage 1 paid projects */}
                    {project.stage === 1 && (project.paymentStatus === 'paid' || project.status === 'paid') && project.receipt && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-green-800">🧾 Payment Receipt</span>
                            <span className="text-xs text-green-600">{project.receipt.name}</span>
                          </div>
                          <a
                            href={project.receipt.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            download={project.receipt.name}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Download
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2 flex-wrap mb-3">
                      {shouldShowPaymentButton(project) && (
                        <button
                          onClick={() => handlePaymentClick(project)}
                          className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                        >
                          {getPaymentButtonText(project)}
                        </button>
                      )}
                      {canApproveProject(project) && (
                        <button
                          onClick={() => approveProject(project)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                        >
                          Approve Project
                        </button>
                      )}
                      {canRequestRevision(project) && (
                        <button
                          onClick={() => handleRevisionRequest(project)}
                          className="bg-orange-600 text-white px-3 py-1 rounded text-sm hover:bg-orange-700"
                        >
                          {getRevisionButtonText(project)}
                        </button>
                      )}

                      {canRejectQuote(project) && (
                        <button
                          onClick={() => handleRejectQuote(project)}
                          className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                        >
                          Reject Quote
                        </button>
                      )}
                    </div>

                    {/* Toggle Buttons for Communications and Deliverables */}
                    <div className="flex gap-2 mb-3">
                      {project.communications && project.communications.length > 0 && (
                        <button
                          onClick={() => toggleMessaging(project.id)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          {showMessaging[project.id] ? '▼' : '▶'} Messages ({project.communications.length})
                        </button>
                      )}
                      {project.deliverables && project.deliverables.length > 0 && (
                        <button
                          onClick={() => toggleDeliverables(project.id)}
                          className="text-xs text-green-600 hover:text-green-800"
                        >
                          {showDeliverables[project.id] ? '▼' : '▶'} Deliverables ({project.deliverables.length})
                        </button>
                      )}
                      {findRelatedProjects(project).length > 0 && (
                        <button
                          onClick={() => toggleRelatedProjects(project.id)}
                          className="text-xs text-purple-600 hover:text-purple-800"
                        >
                          {showRelatedProjects[project.id] ? '▼' : '▶'} Related Projects ({findRelatedProjects(project).length})
                        </button>
                      )}
                    </div>

                    {/* Communications Section */}
                    {showMessaging[project.id] && project.communications && (
                      <div className="mt-3 border-t pt-3">
                        <h4 className="font-semibold text-sm mb-2">Project Communications</h4>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {project.communications.map((comm, idx) => (
                            <div key={idx} className={`p-2 rounded text-xs ${
                              comm.type === 'client' ? 'bg-blue-50 ml-4' :
                              comm.type === 'architect' ? 'bg-green-50 mr-4' :
                              'bg-gray-50'
                            }`}>
                              <div className="font-semibold">{comm.sender}</div>
                              <div>{comm.message}</div>
                              <div className="text-gray-500 text-[10px]">{comm.timestamp}</div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Reply Box */}
                        <div className="mt-3 flex gap-2">
                          <input
                            type="text"
                            value={replyMessages[project.id] || ''}
                            onChange={(e) => setReplyMessages(prev => ({ ...prev, [project.id]: e.target.value }))}
                            placeholder="Type your message..."
                            className="flex-1 px-2 py-1 border rounded text-sm"
                          />
                          <button
                            onClick={() => sendMessage(project.id, replyMessages[project.id])}
                            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                          >
                            Send
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Deliverables Section */}
                    {showDeliverables[project.id] && project.deliverables && (
                      <div className="mt-3 border-t pt-3">
                        <h4 className="font-semibold text-sm mb-2">Project Deliverables</h4>
                        <div className="space-y-2">
                          {project.deliverables.map((deliverable, idx) => (
                            <div key={idx} className="bg-gray-50 p-2 rounded">
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="font-semibold text-sm">{deliverable.title}</div>
                                  <div className="text-xs text-gray-600">{deliverable.description}</div>
                                  <div className="text-[10px] text-gray-500">Delivered: {deliverable.date}</div>
                                </div>
                                <div className="flex gap-1">
                                  {deliverable.files.map((file, fileIdx) => (
                                    <button
                                      key={fileIdx}
                                      onClick={() => downloadFile(project.name, typeof file === 'object' ? file.name : file, typeof file === 'object' ? file.url : null)}
                                      className="text-blue-600 hover:text-blue-800 text-xs"
                                    >
                                      📥
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Related Projects Section */}
                    {showRelatedProjects[project.id] && findRelatedProjects(project).length > 0 && (
                      <div className="mt-3 border-t pt-3">
                        <h4 className="font-semibold text-sm mb-2">Related Projects at this Address</h4>
                        <div className="space-y-1">
                          {findRelatedProjects(project).map(relatedProject => (
                            <div key={relatedProject.id} className="bg-purple-50 p-2 rounded text-xs">
                              <span className="font-semibold">{relatedProject.name}</span> - 
                              Stage {relatedProject.stage} - 
                              Status: {relatedProject.status}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Professional Footer */}
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-[10px] text-gray-500">
                        Prepared by experienced design professionals | Managed entirely through this platform
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-8 text-center py-4 text-xs text-gray-500 border-t">
          planAIdesign - Professional design & documentation by experienced building design experts
        </footer>
      </main>

      {/* Pricing Table Modal */}
      <PricingTableModal 
        isOpen={showPricingTable} 
        onClose={() => setShowPricingTable(false)} 
      />

      <SampleConceptViewer 
        isOpen={showSampleViewer}
        onClose={() => setShowSampleViewer(false)}
      />

      <SampleFeasibilityViewer 
        isOpen={showFeasibilityViewer}
        onClose={() => setShowFeasibilityViewer(false)}
      />

    </div>
  );
}